import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../components/architecture-diagram';
import '../components/ui/ui-button';
import { navigate } from '../router';
import { cardStyles, motionStyles, pageFrameStyles, statStyles } from '../styles/lit-shared';

const ROLES = [
  {
    title: 'Director',
    description:
      'Analiza tu producto o idea, define el ángulo y coordina al equipo. No redacta: manda. Es el jefe que no hace de becario.',
    route: '/blunticle/agentes/director',
  },
  {
    title: 'Redactores',
    description:
      'Escriben el artículo y los posts de redes. Cada uno en su línea, con tu contexto y lo que han sacado de tu contenido. Uno no se mete en el trabajo del otro.',
    route: '/blunticle/agentes/writers',
  },
  {
    title: 'Secretario',
    description:
      'Auditor final. Si algo suena raro, genérico o a ChatGPT de feria, lo detecta y lo arregla antes de que lo veas tú.',
    route: '/blunticle/agentes/secretario',
  },
  {
    title: 'Artista',
    description:
      'Genera la imagen que acompaña al contenido. Sin stock photo de señor señalando un gráfico con cara de felicidad falsa.',
    route: '/blunticle/agentes/artista',
  },
];

@customElement('blunticle-page')
export class BlunticlePage extends LitElement {
  private goTo(path: string) {
    void navigate(path);
  }

  static styles = [
    pageFrameStyles,
    cardStyles,
    motionStyles,
    statStyles,
    css`
      .hero {
        position: relative;
        margin-bottom: var(--space-2xl);
        padding: var(--space-2xl) var(--space-xl);
        border-radius: var(--radius-lg);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        overflow: hidden;
        text-align: center;
      }

      .hero::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -10%;
        width: 300px;
        height: 300px;
        border-radius: 50%;
        background: var(--glow-violet);
        filter: blur(50px);
        pointer-events: none;
      }

      .hero-content {
        position: relative;
        z-index: 1;
      }

      .eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        margin-bottom: var(--space-md);
        padding: 0.25rem 0.75rem;
        border-radius: var(--radius-full);
        background: rgb(255 255 255 / 6%);
        border: 1px solid var(--color-border);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-accent);
      }

      .brand-origin {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        margin-bottom: var(--space-sm);
        font-family: var(--font-mono);
        font-size: 0.8125rem;
        font-weight: 500;
        letter-spacing: 0.04em;
        color: var(--color-text-subtle);
      }

      .origin-blunt {
        color: var(--color-accent);
        font-weight: 600;
      }

      .origin-plus {
        opacity: 0.45;
        font-weight: 400;
      }

      .origin-article {
        color: var(--color-primary-light);
        font-weight: 600;
      }

      .brand-origin-container {
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-content: center;
        align-items: center;
      }

      h1 {
        font-size: clamp(2rem, 5vw, 3rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        margin-bottom: var(--space-md);
        color: var(--color-text);
      }

      .brand-name {
        white-space: nowrap;
      }

      .brand-blunt {
        color: var(--color-accent);
      }

      .brand-icle {
        color: var(--color-primary-light);
      }

      .hero-lead {
        font-size: 1.125rem;
        color: var(--color-text-muted);
        max-width: 52ch;
        margin: 0 auto var(--space-xl);
        line-height: 1.6;
      }

      .hero-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-md);
        justify-content: center;
        margin-bottom: var(--space-xl);
      }

      .section-title {
        font-size: clamp(1.5rem, 3vw, 1.875rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        margin-bottom: var(--space-sm);
      }

      .section-lead {
        color: var(--color-text-muted);
        font-size: 1rem;
        margin-bottom: var(--space-xl);
        max-width: 56ch;
      }

      .features {
        display: grid;
        gap: var(--space-md);
        grid-template-columns: 1fr;
        margin-bottom: var(--space-2xl);
      }

      @media (min-width: 700px) {
        .features {
          grid-template-columns: repeat(2, 1fr);
        }

        .stat-grid {
          grid-template-columns: repeat(4, 1fr);
        }
      }

      .feature h3 {
        margin: 0 0 var(--space-xs);
        font-size: 1.0625rem;
      }

      .feature p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
      }

      .stat-card--link {
        cursor: pointer;
      }

      .stat-card--link .stat-arrow {
        font-size: 0.75rem;
        color: var(--color-text-muted);
        margin-top: var(--space-xs);
        opacity: 0;
        transition: opacity var(--duration-normal) var(--ease-out);
      }

      .stat-card--link:hover .stat-arrow {
        opacity: 1;
      }

      /* Architecture section */
      .arch-section {
        margin-top: var(--space-2xl);
        padding-top: var(--space-2xl);
        border-top: 1px solid var(--color-border);
      }

      .arch-intro {
        max-width: 62ch;
        margin-bottom: var(--space-2xl);
      }

      .arch-intro p {
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.65;
      }

      h2 {
        font-size: 1.375rem;
        margin: var(--space-2xl) 0 var(--space-md);
      }

      .flow-steps {
        display: grid;
        gap: var(--space-md);
      }

      .step {
        display: flex;
        gap: var(--space-md);
        align-items: flex-start;
      }

      .step-num {
        flex-shrink: 0;
        width: 2rem;
        height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-full);
        background: var(--gradient-primary);
        color: var(--palette-white);
        font-weight: 700;
        font-size: 0.875rem;
      }

      .step h3 {
        margin: 0 0 0.25rem;
        font-size: 1rem;
      }

      .step p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .roles {
        display: grid;
        gap: var(--space-md);
      }

      @media (min-width: 640px) {
        .roles {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      .role {
        cursor: pointer;
      }

      .role h3 {
        margin: 0 0 var(--space-xs);
        font-size: 1.0625rem;
      }

      .role p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .role-arrow {
        display: block;
        margin-top: var(--space-sm);
        font-size: 0.8125rem;
        color: var(--color-text-muted);
        opacity: 0;
        transition: opacity var(--duration-normal) var(--ease-out);
      }

      .role:hover .role-arrow {
        opacity: 1;
      }

      .campaign p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .note {
        margin-top: var(--space-2xl);
        padding: var(--space-lg);
        border-radius: var(--radius);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        font-size: 0.9375rem;
        color: var(--color-text-muted);
        line-height: 1.6;
      }
    `,
  ];

  render() {
    return html`
      <section class="hero animate-fade-up">
        <div class="hero-content">
          <div class="brand-origin-container">
            <span class="eyebrow">Mi herramienta de contenido</span>
            <p class="brand-origin" aria-label="Blunt más Article">
              <span class="origin-blunt">Blunt</span>
              <span class="origin-plus" aria-hidden="true">+</span>
              <span class="origin-article">Article</span>
            </p>
          </div>
          <h1 style="margin-bottom: 0px;">
            <span class="brand-name"
              ><span class="brand-blunt">Blunt</span><span class="brand-icle">icle</span></span
            >
          </h1>
          <h1 style="margin-top: 0px;">Rompe el Folio en Blanco por Ti 💥</h1>
          <p class="hero-lead">
            Tú traes la idea — un producto, un post, lo que sea — y te devuelvo una base de
            contenido para editar, publicar o mandar al limbo sin dramas. No es magia: es un
            equipito que se pone a currar mientras tú respiras.
          </p>
          <div class="hero-actions">
            <ui-button href="https://blunticle.alejandrosn.com" variant="primary">Probar Blunticle</ui-button>
          </div>
          <div class="stat-grid">
            <div class="stat-card stat-card--link" @click=${() => this.goTo('/blunticle/agentes/director')}>
              <div class="stat-label">Dirección</div>
              <div class="stat-value">El Director</div>
              <div class="stat-arrow">Ver más →</div>
            </div>
            <div class="stat-card stat-card--link" @click=${() => this.goTo('/blunticle/agentes/secretario')}>
              <div class="stat-label">Calidad</div>
              <div class="stat-value">El Secretario</div>
              <div class="stat-arrow">Ver más →</div>
            </div>
            <div class="stat-card stat-card--link" @click=${() => this.goTo('/blunticle/agentes/writers')}>
              <div class="stat-label">Redacción</div>
              <div class="stat-value">Los Writers</div>
              <div class="stat-arrow">Ver más →</div>
            </div>
            <div class="stat-card stat-card--link" @click=${() => this.goTo('/blunticle/agentes/artista')}>
              <div class="stat-label">Visual</div>
              <div class="stat-value">El Artista</div>
              <div class="stat-arrow">Ver más →</div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2 class="section-title">Para qué sirve esto</h2>
        <p class="section-lead">
          Lo monté porque el cursor parpadeando en una página vacía me ponía de los nervios.
          Blunticle es mi forma de decirle al bloqueo creativo que se vaya a tomar por saco.
        </p>
        <div class="features">
          <article class="card feature">
            <h3>Romper el folio en blanco</h3>
            <p>
              Tienes la chispa pero no las palabras. Blunticle te saca del atasco con un
              borrador de verdad, no con un "escribe algo aquí" y suerte.
            </p>
          </article>
          <article class="card feature">
            <h3>Tu idea, su contexto</h3>
            <p>
              Cada agente trabaja con lo que tú le cuentas y con lo que saca de tu propio
              contenido: tu web, tus textos, lo que le pases. Nada de inventarse la vida.
            </p>
          </article>
          <article class="card feature">
            <h3>Cada uno a lo suyo</h3>
            <p>
              Jerarquía clarísima: nadie hace de todo. Cada responsable tiene una tarea
              concreta y no se mete en el fregado del de al lado.
            </p>
          </article>
          <article class="card feature">
            <h3>Base, no verdad absoluta</h3>
            <p>
              Lo que sale es punto de partida. Tú retocas, publicas o lo tiras a la papelera.
              Sin pretensiones de Pulitzer, prometido.
            </p>
          </article>
        </div>
      </section>

      <!-- Architecture section -->
      <div class="arch-section animate-fade-up">
        <div class="arch-intro">
          <h2 class="section-title">Arquitectura</h2>
          <p>
            Blunticle no es un chat que te escupe texto a lo loco. Lo monté como una empresa
            pequeñísima pero muy organizada — cada uno sabe lo suyo y nadie se pone a improvisar
            el trabajo del otro.
          </p>
        </div>

        <architecture-diagram></architecture-diagram>

        <h2>El organigrama</h2>
        <div class="flow-steps">
          <div class="step">
            <span class="step-num">1</span>
            <div>
              <h3>Tú traes la chispa</h3>
              <p>
                Una URL, un producto, una idea a medias. Eso es el contexto que arranca todo.
                Sin eso, ni el Director sabe por dónde tirar.
              </p>
            </div>
          </div>
          <div class="step">
            <span class="step-num">2</span>
            <div>
              <h3>El Director lo entiende</h3>
              <p>
                Lee la situación, define el enfoque y reparte el trabajo. Mira tu contenido,
                saca conclusiones y dice quién hace qué. Sin micromanagement.
              </p>
            </div>
          </div>
          <div class="step">
            <span class="step-num">3</span>
            <div>
              <h3>Los Redactores escriben</h3>
              <p>
                Artículo, posts para redes… cada uno en su línea, con tu contexto y lo que han
                extraído de tu propio material. En paralelo, sin pisarse los talones.
              </p>
            </div>
          </div>
          <div class="step">
            <span class="step-num">4</span>
            <div>
              <h3>El Secretario cierra</h3>
              <p>
                Revisa, corrige y deja todo presentable. Si algo huele a relleno, lo manda de
                vuelta. Luego el Artista pone la imagen si toca. Y a ti te llega la base lista.
              </p>
            </div>
          </div>
        </div>

        <h2>Los puestos</h2>
        <div class="roles">
          ${ROLES.map(
            (role) => html`
              <article class="card role" @click=${() => this.goTo(role.route)}>
                <h3>${role.title}</h3>
                <p>${role.description}</p>
                <span class="role-arrow">Ver más →</span>
              </article>
            `,
          )}
        </div>

        <h2>Modo campañas</h2>
        <div class="card campaign">
          <p>
            ¿Quieres contar una historia de marca entera y no un post suelto? El modo campañas
            planifica una temporada de contenido — diez capítulos con hilo conductor — y genera
            fase a fase o de golpe. Como una serie, pero para vender tu idea sin parecer un
            robot en LinkedIn.
          </p>
        </div>

        <div class="note">
          Esto lo programé porque estaba harto del cursor parpadeando en una página vacía.
          Si te sirve, genial. Si no, al menos ya no estás solo ante el folio en blanco.
        </div>

        <div style="margin-top: var(--space-xl)">
          <ui-button href="https://blunticle.alejandrosn.com" variant="primary">Ir a Blunticle</ui-button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'blunticle-page': BlunticlePage;
  }
}
